import { Loader2 } from 'lucide-react'

import type { ExternalApplyFormApi } from '@/components/external-apply/external-apply-form-state'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

export function ExternalApplyFields({ form }: { form: ExternalApplyFormApi }) {
  return (
    <form className="space-y-6" onSubmit={form.handleSubmit}>
      <section className="grid gap-4 md:grid-cols-2">
        <FormField label="First name" htmlFor="apply-first-name">
          <Input
            id="apply-first-name"
            className="placeholder:italic"
            placeholder="First name"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(event) => form.setFirstName(event.target.value)}
          />
        </FormField>
        <FormField label="Last name" htmlFor="apply-last-name">
          <Input
            id="apply-last-name"
            className="placeholder:italic"
            placeholder="Last name"
            autoComplete="family-name"
            value={form.lastName}
            onChange={(event) => form.setLastName(event.target.value)}
          />
        </FormField>
        <FormField label="Email address" htmlFor="apply-email" className="md:col-span-2">
          <Input
            id="apply-email"
            className="placeholder:italic"
            placeholder="Email address"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => form.setEmail(event.target.value)}
          />
        </FormField>
      </section>

      <section className="space-y-4 border-t pt-4">
        <FormField label="Cover letter (optional)" htmlFor="apply-cover-letter">
          <Textarea
            id="apply-cover-letter"
            className="min-h-[120px]"
            placeholder="Tell us why you are interested in this role..."
            value={form.coverLetter}
            onChange={(event) => form.setCoverLetter(event.target.value)}
          />
        </FormField>
        <FormField label="Resume (optional)" htmlFor="apply-resume" description="PDF or Word, up to 10 MB.">
          <Input
            id="apply-resume"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf"
            onChange={(event) => form.setResumeFile(event.target.files?.[0] ?? null)}
          />
        </FormField>
      </section>

      {form.questions.length > 0 && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Screening questions</h2>
            <p className="text-sm text-muted-foreground">Answer these before we send your secure link.</p>
          </div>
          {form.questions.map((question) => (
            <FormField
              key={question.id}
              label={
                <>
                  {question.question_text}
                  {question.is_required ? ' *' : ''}
                </>
              }
              htmlFor={`apply-question-${question.id}`}
            >
              <Textarea
                id={`apply-question-${question.id}`}
                required={question.is_required}
                placeholder="Your answer"
                value={form.answers[question.id] || ''}
                onChange={(event) => form.setAnswer(question.id, event.target.value)}
              />
            </FormField>
          ))}
        </section>
      )}

      {form.error ? (
        <p className="text-sm text-destructive" role="alert">
          {form.error}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground">You will finish signup from a secure email link.</p>
        <Button type="submit" disabled={form.isSubmitting}>
          {form.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Send secure link
        </Button>
      </div>
    </form>
  )
}
